import { useCallback } from "react";
import { useCommentAction } from "@/hooks/use-comment-action";
import { useFrameReply } from "@/components/shapes/shared/use-frame-reply";
import { SelectionComposer } from "./selection-composer";

interface SelectionFormProps {
  frameId: string;
  frameTitle: string;
  frameType: string;
  selectedText: string;
  /** Escape or close: back to the toolbar buttons. */
  onCancel: () => void;
  /** The request was sent. */
  onSent: () => void;
}

/**
 * "Ask Denker" about a selection: continues the frame's conversation with the
 * selected text quoted, optionally directed at a mentioned agent. Shared by
 * the canvas selection toolbar and the Doc pill.
 */
export function SelectionAskForm({
  frameId,
  frameTitle,
  frameType,
  selectedText,
  onCancel,
  onSent,
}: SelectionFormProps) {
  const comment = useCommentAction({ frameId, frameTitle, frameType });
  const reply = useFrameReply(frameId);

  const submit = useCallback(async () => {
    const request = comment.text.trim();
    if (!request) return;
    const directedRequest = comment.selectedAgent
      ? `@${comment.selectedAgent.name}\n${request}`
      : request;
    const sent = await reply.send(
      `Regarding this selected text in frame "${frameTitle}":\n> ${selectedText}\n\n${directedRequest}`,
    );
    if (!sent) return;
    comment.handleTextChange("");
    comment.clearAgent();
    onSent();
  }, [comment, frameTitle, onSent, reply, selectedText]);

  return (
    <SelectionComposer
      comment={comment}
      selectedText={selectedText}
      dictationId={`selection-ask:${frameId}:${selectedText}`}
      canSubmit={reply.canSend && !reply.isSending}
      onSubmit={submit}
      onCancel={onCancel}
    />
  );
}
